import React, { useState } from 'react';
import { MedicalRecord, getSafeUrl } from './AppContext';
import { Button } from './Components';

interface MedicalRecordMetadataModalProps {
  record: MedicalRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenPrescriptionModal?: (record: MedicalRecord) => void;
  onDelete?: (recordId: string) => void;
}

export const MedicalRecordMetadataModal: React.FC<MedicalRecordMetadataModalProps> = ({
  record,
  isOpen,
  onClose,
  onOpenPrescriptionModal,
  onDelete
}) => {
  const [copiedId, setCopiedId] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'clinical' | 'files' | 'audit'>('overview');

  if (!isOpen || !record) return null;

  // Resolve all attachments: supports both single attachmentUrl and multiple attachments array
  const allAttachments: { name: string; url: string; size?: number; type?: string }[] = [];
  if (Array.isArray(record.attachments) && record.attachments.length > 0) {
    allAttachments.push(...record.attachments);
  } else if (record.attachmentUrl) {
    allAttachments.push({
      name: record.attachmentName || 'Attached Document',
      url: record.attachmentUrl
    });
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes || isNaN(bytes)) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '')) {
      return 'fa-file-image text-emerald-600 bg-emerald-50';
    }
    if (ext === 'pdf') {
      return 'fa-file-pdf text-rose-600 bg-rose-50';
    }
    if (['doc', 'docx'].includes(ext || '')) {
      return 'fa-file-word text-blue-600 bg-blue-50';
    }
    return 'fa-file-lines text-indigo-600 bg-indigo-50';
  };

  const handleCopyId = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const isDoctorReport = record.authoredBy === 'doctor' || (record.doctorName && record.doctorName !== 'Self-Reported');
  const recordCategory = record.prescription && record.prescription !== 'N/A' && !isDoctorReport 
    ? record.prescription 
    : (isDoctorReport ? 'Clinical Consultation Report' : 'Patient Uploaded Document');

  return (
    <div className="fixed inset-0 z-[650] flex items-center justify-center p-3 sm:p-5 md:p-6 bg-slate-950/70 backdrop-blur-md overflow-y-auto animate-in fade-in duration-200">
      <div 
        className="bg-white w-full max-w-3xl rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col my-auto max-h-[90vh] border border-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-100 bg-slate-50/70 flex justify-between items-start gap-4 shrink-0">
          <div className="flex items-start gap-3.5">
            <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-lg shrink-0 ${
              isDoctorReport ? 'bg-emerald-50 text-emerald-700' : 'bg-indigo-50 text-indigo-700'
            }`}>
              <i className={isDoctorReport ? "fa-solid fa-notes-medical" : "fa-solid fa-file-shield"}></i>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                  isDoctorReport 
                    ? 'bg-emerald-100/70 text-emerald-800 border border-emerald-200' 
                    : 'bg-indigo-100/70 text-indigo-800 border border-indigo-200'
                }`}>
                  {recordCategory}
                </span>
                {record.lastEditedAt && (
                  <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <i className="fa-solid fa-clock-rotate-left"></i> Revised
                  </span>
                )}
              </div>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight mt-1 leading-snug">
                {record.diagnosis || 'Medical Record & Document Specification'}
              </h3>
              <p className="text-slate-400 font-bold text-[11px] mt-0.5">
                <i className="fa-regular fa-calendar mr-1"></i> Recorded on {record.date} • Authored by {record.doctorName || 'Self'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-slate-400 hover:text-slate-600 transition-colors w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-xs shrink-0"
            title="Close"
          >
            <i className="fa-solid fa-xmark text-base"></i>
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-slate-100 bg-white px-5 sm:px-6 pt-3 gap-2 overflow-x-auto scrollbar-none shrink-0">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'overview'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <i className="fa-solid fa-circle-info"></i> Identification & Meta
          </button>
          <button
            onClick={() => setActiveTab('clinical')}
            className={`pb-3 px-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'clinical'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <i className="fa-solid fa-stethoscope"></i> Clinical Notes & Findings
          </button>
          <button
            onClick={() => setActiveTab('files')}
            className={`pb-3 px-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'files'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <i className="fa-solid fa-paperclip"></i> Attachments ({allAttachments.length})
          </button>
          {record.editHistory && record.editHistory.length > 0 && (
            <button
              onClick={() => setActiveTab('audit')}
              className={`pb-3 px-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
                activeTab === 'audit'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <i className="fa-solid fa-timeline"></i> Revision Audit ({record.editHistory.length})
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-6 md:p-8 space-y-6 overflow-y-auto flex-1 scrollbar-thin min-h-0 bg-slate-50/30">

          {/* TAB 1: OVERVIEW & SYSTEM METADATA */}
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Record Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-2xs">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Unique Record ID</p>
                  <div className="flex items-center justify-between gap-1 mt-1">
                    <p className="text-xs font-bold text-slate-900 font-mono truncate" title={record.id}>
                      {record.id}
                    </p>
                    <button
                      onClick={() => handleCopyId(record.id)}
                      className="text-slate-400 hover:text-indigo-600 p-1 text-xs"
                      title="Copy Record ID"
                    >
                      <i className={copiedId ? "fa-solid fa-check text-emerald-600" : "fa-regular fa-copy"}></i>
                    </button>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-2xs">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">e-Prescription (Rx) Ref</p>
                  <p className="text-xs font-bold text-slate-900 font-mono mt-1">
                    {record.rxId ? `#RX-${record.rxId}` : 'Not Prescribed'}
                  </p>
                </div>

                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-2xs">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Associated Appointment</p>
                  <p className="text-xs font-bold text-slate-900 truncate mt-1">
                    {record.appointmentId ? `Ref: ${record.appointmentId.slice(0, 14)}...` : 'Direct Portal Vault'}
                  </p>
                </div>
              </div>

              {/* Author & Patient Information */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-4">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                  <i className="fa-solid fa-user-doctor text-indigo-500"></i> Doctor & Patient Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">Authoring Provider</label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-bold shrink-0">
                        <i className={isDoctorReport ? "fa-solid fa-user-doctor" : "fa-solid fa-user"}></i>
                      </span>
                      <div>
                        <p className="text-xs font-black text-slate-900">{record.doctorName || 'Self-Reported'}</p>
                        <p className="text-[10px] text-slate-400 font-medium">Provider ID: {record.doctorId || 'self'}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">Patient</label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center text-xs font-bold shrink-0">
                        <i className="fa-solid fa-id-card"></i>
                      </span>
                      <div>
                        <p className="text-xs font-black text-slate-900">{record.patientName || 'Patient'}</p>
                        <p className="text-[10px] text-slate-400 font-medium">
                          MRN: {record.patientMrn || 'N/A'} • UID: {record.patientId ? record.patientId.slice(0, 10) + '...' : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timestamp Details */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-3">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                  <i className="fa-regular fa-clock text-indigo-500"></i> Date & Creation History
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Clinical Date</span>
                    <span className="font-black text-slate-800">{record.date}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Created Timestamp</span>
                    <span className="font-mono text-slate-700 text-[11px]">
                      {record.createdAt ? new Date(record.createdAt).toLocaleString() : (record.timestamp ? new Date(record.timestamp).toLocaleString() : record.date)}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Document Source</span>
                    <span className="font-bold text-indigo-600 uppercase text-[11px]">
                      {record.authoredBy === 'doctor' ? 'Clinical Telemedicine Portal' : 'Patient Secure Vault'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: CLINICAL PAYLOAD & NOTES */}
          {activeTab === 'clinical' && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Diagnosis / Assessment / Title</label>
                <p className="text-base font-black text-slate-900">{record.diagnosis || 'None Documented'}</p>
              </div>

              {record.vitalSigns && (
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Recorded Vitals</label>
                  <p className="text-xs font-bold text-indigo-700 bg-indigo-50 p-3 rounded-xl font-mono">{record.vitalSigns}</p>
                </div>
              )}

              {record.prescription && record.prescription !== 'N/A' && (
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Official Prescription (e-Rx)</label>
                    {onOpenPrescriptionModal && isDoctorReport && (
                      <button
                        onClick={() => onOpenPrescriptionModal(record)}
                        className="text-xs font-black text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                      >
                        <i className="fa-solid fa-receipt"></i> Open Full e-Rx View
                      </button>
                    )}
                  </div>
                  <p className="text-xs font-bold text-slate-800 whitespace-pre-wrap bg-emerald-50/60 border border-emerald-100 p-3.5 rounded-xl leading-relaxed">
                    {record.prescription}
                  </p>
                </div>
              )}

              {record.treatmentPlan && (
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Treatment Plan</label>
                  <p className="text-xs font-medium text-slate-700 whitespace-pre-wrap bg-slate-50 p-3.5 rounded-xl leading-relaxed">
                    {record.treatmentPlan}
                  </p>
                </div>
              )}

              {record.followUpInstructions && (
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Follow-Up Instructions</label>
                  <p className="text-xs font-medium text-slate-700 whitespace-pre-wrap bg-slate-50 p-3.5 rounded-xl leading-relaxed">
                    {record.followUpInstructions}
                  </p>
                </div>
              )}

              {record.notes && (
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Clinical / Personal Remarks</label>
                  <p className="text-xs font-medium text-slate-700 whitespace-pre-wrap bg-slate-50 p-3.5 rounded-xl leading-relaxed">
                    {record.notes}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: ATTACHMENTS & UPLOADED FILES */}
          {activeTab === 'files' && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <div className="flex items-center justify-between">
                <p className="text-xs font-black uppercase tracking-wider text-slate-400">
                  Attached Files ({allAttachments.length})
                </p>
                <span className="text-[11px] font-bold text-slate-500">
                  Stored securely in ROVITA Clinical Storage
                </span>
              </div>

              {allAttachments.length === 0 ? (
                <div className="bg-white p-10 rounded-2xl border border-dashed border-slate-200 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center text-xl mx-auto mb-2">
                    <i className="fa-solid fa-file-circle-xmark"></i>
                  </div>
                  <h5 className="font-bold text-slate-800 text-sm">No External Files Attached</h5>
                  <p className="text-xs text-slate-400 mt-1">This entry consists solely of text-based clinical notes and observations.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allAttachments.map((file, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white p-4 rounded-2xl border border-slate-200/90 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-indigo-200 transition-all"
                    >
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-base shrink-0 ${getFileIcon(file.name)}`}>
                          <i className={`fa-solid ${getFileIcon(file.name).split(' ')[0]}`}></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-black text-slate-900 truncate" title={file.name}>
                            {file.name}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400 font-bold">
                            <span>Attachment #{idx + 1}</span>
                            {file.size && <span>• {formatFileSize(file.size)}</span>}
                            {file.type && <span>• {file.type}</span>}
                          </div>
                        </div>
                      </div>

                      <a
                        href={getSafeUrl(file.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="py-2 px-4 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs transition-all flex items-center justify-center gap-1.5 self-start sm:self-auto shrink-0 shadow-2xs"
                      >
                        <i className="fa-solid fa-arrow-up-right-from-square"></i>
                        Open / Download
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: AUDIT HISTORY */}
          {activeTab === 'audit' && record.editHistory && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <p className="text-xs font-black uppercase tracking-wider text-slate-400">
                Modification History & Compliance Audit Trail
              </p>
              <div className="space-y-3">
                {record.editHistory.map((item, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-black text-slate-900">
                        Revision #{record.editHistory!.length - idx}
                      </span>
                      <span className="text-[11px] font-bold text-slate-400">
                        {new Date(item.editedAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-[11px] font-bold text-indigo-600">
                      Amended by Dr. {item.editedByDoctorName} (ID: {item.editedByDoctorId})
                    </p>
                    {item.summaryOfChanges && (
                      <p className="text-xs font-medium text-slate-600 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                        "{item.summaryOfChanges}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 sm:p-6 bg-slate-50/90 backdrop-blur-md flex flex-wrap items-center justify-between gap-3 shrink-0 border-t border-slate-100">
          <div className="text-[11px] text-slate-400 font-bold">
            HIPAA & HITECH Medical Metadata Compliant
          </div>
          <div className="flex items-center gap-2">
            {!isDoctorReport && onDelete && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Are you sure you want to delete "${record.diagnosis || 'this report'}"? This cannot be undone.`)) {
                    onDelete(record.id);
                    onClose();
                  }
                }}
                className="py-2.5 px-3.5 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white font-bold text-xs rounded-2xl transition-all border border-rose-200 flex items-center gap-1.5 active:scale-95"
                title="Delete this medical report"
              >
                <i className="fa-solid fa-trash-can"></i>
                <span>Delete Report</span>
              </button>
            )}
            <Button onClick={onClose} variant="ghost" className="py-2.5 px-4 text-xs font-bold">
              Close
            </Button>
            {allAttachments.length > 0 && (
              <a
                href={getSafeUrl(allAttachments[0].url)}
                target="_blank"
                rel="noopener noreferrer"
                className="py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-2xl transition-all shadow-sm flex items-center gap-1.5"
              >
                <i className="fa-solid fa-arrow-down"></i>
                Download Primary File
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
